import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import "moment/locale/tr";
import * as action from "../Store/_redux/LeaveStore/leaveAction";
import { LeaveStateEnum } from "../modal/leave-state";
import { LeaveTypeEnum } from "../modal/leave-type";
import * as authAction from "../Store/_redux/AuthStore/authActions";

moment.locale("tr");

const TYPE_COLORS_HEX = {
  [LeaveTypeEnum.ANNUAL]:    "#6366f1",  // 💜 İndigo
  [LeaveTypeEnum.SICK]:      "#10b981",  // 💚 Zümrüt Yeşili
  [LeaveTypeEnum.UNPAID]:    "#f59e0b",  // 🟡 Amber
  [LeaveTypeEnum.MATERNITY]: "#ec4899",  // 🩷 Hot Pink
  "Doğum Günü":     "#59d3fc",  // 🟠 Turkuaz
};

function LeaveCalendar() {
  const users = useSelector((state) => state.auth.users) || [];
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth.loginUser);
  const formLeave = useSelector((state) => state.leave.leaves) || [];
  const [currentDate, setCurrentDate] = useState(moment());
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    dispatch(action.getLeave(auth));
    dispatch(authAction.getUsers());
  }, [dispatch, auth]);
 
  const startOfCalendar = currentDate.clone().startOf("month").startOf("isoWeek");
  const endOfCalendar = currentDate.clone().endOf("month").endOf("isoWeek");

  const days = [];
  const day = startOfCalendar.clone();
  while (day.isSameOrBefore(endOfCalendar)) {
    days.push(day.clone());
    day.add(1, "day");
  }

  const getEventsForDay = (d) => {
    const leaves = formLeave.filter((l) => {
      if (!l.startDate || !l.finishDate) return false;
      const start = moment(l.startDate).startOf("day");
      const end = moment(l.finishDate).endOf("day");
      return d.isSameOrAfter(start) && d.isSameOrBefore(end);
    });
    const birthdays = users.reduce((acc, user) => {
      if (!user.birthDay) return acc;
      const birthday = moment.utc(user.birthDay).startOf("day");
      if(d.month() === birthday.month() && d.date() === birthday.date()) {
        acc.push({
          leavesType: "Doğum Günü",
          owner: user,
          startDate: moment(d).startOf("day"),
          finishDate: moment(d).endOf("day"),
          state: LeaveStateEnum.APPROVED,
        });
      }
      return acc;
    }, []);
    
    return [...leaves, ...birthdays];
  };

  const weekDays = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-semibold mb-4">Takvim</h1>

      <div className="flex flex-wrap gap-3 mb-4">
        {Object.entries(TYPE_COLORS_HEX).map(([type, color]) => (
          <div key={type} className="flex items-center gap-1.5 text-sm">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
            <span className="text-gray-600">{type}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5 text-sm">
          <div className="w-3 h-3 rounded-full bg-gray-400" />
          <span className="text-gray-600">Beklemede</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <span className="text-gray-600">Reddedildi</span>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex gap-2">
            <button onClick={() => setCurrentDate(currentDate.clone().subtract(1, "month"))} className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50">&#8249;</button>
            <button onClick={() => setCurrentDate(moment())} className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50">Bugün</button>
            <button onClick={() => setCurrentDate(currentDate.clone().add(1, "month"))} className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50">&#8250;</button>
          </div>
          <h2 className="text-lg font-semibold capitalize">{currentDate.format("MMMM YYYY")}</h2>
          <div className="w-28" />
        </div>

        <div className="grid grid-cols-7 border-b">
          {weekDays.map((d) => (
            <div key={d} className="text-center text-xs font-medium text-gray-500 py-2">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {days.map((d, i) => {
            const isCurrentMonth = d.month() === currentDate.month();
            const isToday = d.isSame(moment(), "day");
            const events = getEventsForDay(d);
            return (
              <div key={i} className={`min-h-[90px] border-b border-r p-1 ${!isCurrentMonth ? "bg-gray-50" : "bg-white"}`}>
                <div className={`text-xs w-6 h-6 flex items-center justify-center rounded-full mb-1 ${isToday ? "bg-blue-600 text-white font-bold" : isCurrentMonth ? "text-gray-700" : "text-gray-300"}`}>
                  {d.date()}
                </div>
                <div className="space-y-0.5">
                  {events.slice(0, 3).map((evt, j) => {
                    const color = evt.state === LeaveStateEnum.PENDING ? "#9ca3af" : evt.state === LeaveStateEnum.REJECTED ? "#8b0000" : TYPE_COLORS_HEX[evt.leavesType] || "#0000e0";
                    return (
                      <div key={j} onClick={() => setSelectedEvent(evt)} className="text-white text-[10px] px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80" style={{ backgroundColor: color }}>
                        {evt.owner?.name} {evt.owner?.surname}
                      </div>
                    );
                  })}
                  {events.length > 3 && <div className="text-[10px] text-gray-400 pl-1">+{events.length - 3} daha</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50" onClick={() => setSelectedEvent(null)}>
          <div className="bg-white rounded-2xl p-6 w-80 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4 border-b pb-2">İzin Detayı</h2>
            <div className="space-y-2 text-sm text-gray-700">
              <div><span className="font-medium">Çalışan:</span> {selectedEvent.owner?.name} {selectedEvent.owner?.surname}</div>
              <div><span className="font-medium">TCKN:</span> {selectedEvent.owner?.tc}</div>
              <div><span className="font-medium">İzin Türü:</span> {selectedEvent.leavesType}</div>
              <div><span className="font-medium">Başlangıç:</span> {moment(selectedEvent.startDate).format("DD.MM.YYYY")}</div>
              <div><span className="font-medium">Bitiş:</span> {moment(selectedEvent.finishDate).format("DD.MM.YYYY")}</div>
              <div><span className="font-medium">Gün Sayısı:</span> {selectedEvent.totalDate}</div>
              <div>
                <span className="font-medium">Durum:</span>{" "}
                <span className={`px-2 py-0.5 rounded text-white text-xs ${selectedEvent.state === LeaveStateEnum.APPROVED ? "bg-green-500" : selectedEvent.state === LeaveStateEnum.REJECTED ? "bg-red-500" : "bg-yellow-400"}`}>
                  {selectedEvent.state === LeaveStateEnum.APPROVED ? "Onaylandı" : selectedEvent.state === LeaveStateEnum.REJECTED ? "Reddedildi" : "Bekliyor"}
                </span>
              </div>
              {selectedEvent.comment && <div><span className="font-medium">Açıklama:</span> {selectedEvent.comment}</div>}
            </div>
            <button onClick={() => setSelectedEvent(null)} className="mt-4 w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition">Kapat</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeaveCalendar;